import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Play, Pause, RotateCcw } from 'lucide-react';

interface TimerProps {
  duration: number;
  isRunning: boolean;
  setIsRunning: (isRunning: boolean) => void;
  onComplete: () => void;
  onDurationChange?: (duration: number) => void;
}

export function Timer({ duration, isRunning, setIsRunning, onComplete, onDurationChange }: TimerProps) {
  const [timeLeft, setTimeLeft] = useState(duration);
  const [isWarning, setIsWarning] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const resetTimer = useCallback(() => {
    setTimeLeft(duration);
    setIsRunning(false);
    setIsWarning(false);
  }, [duration, setIsRunning]);

  useEffect(() => {
    setTimeLeft(duration);
  }, [duration]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isRunning && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => {
          // Set warning state when 1 minute remaining
          if (prevTime === 60) {
            setIsWarning(true);
            if (audioRef.current) {
              audioRef.current.play().catch(console.error);
            }
          }

          if (prevTime <= 1) {
            clearInterval(timer);
            setIsRunning(false);
            setIsWarning(false);
            onComplete();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [isRunning, timeLeft, onComplete, setIsRunning]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    if (!isRunning) return 'text-gray-700 dark:text-gray-300';
    if (isWarning) return 'text-red-500';
    return 'text-orange-500';
  };

  return (
    <div className="flex items-center gap-6">
      <div className="flex flex-col items-center gap-2">
        <div className={`text-6xl font-mono font-bold transition-colors duration-300 ${getTimerColor()} bg-gray-100 dark:bg-gray-800 px-6 py-4 rounded-xl shadow-inner`}>
          {formatTime(timeLeft)}
        </div>
        {onDurationChange && (
          <select
            value={duration}
            onChange={(e) => {
              const newDuration = parseInt(e.target.value);
              onDurationChange(newDuration);
              setTimeLeft(newDuration);
              setIsRunning(false);
            }}
            className="px-3 py-1 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value={1200}>20 minutes</option>
            <option value={900}>15 minutes</option>
            <option value={600}>10 minutes</option>
            <option value={300}>5 minutes</option>
            <option value={120}>2 minutes</option>
            <option value={60}>1 minute</option>
          </select>
        )}
      </div>
      <audio ref={audioRef} src="/sounds/timer-warning.mp3" />
      <div className="flex flex-col gap-3">
        <button
          onClick={() => setIsRunning(!isRunning)}
          className={`p-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
            isRunning
              ? 'bg-orange-500 hover:bg-orange-600 text-white'
              : 'bg-red-500 hover:bg-red-600 text-white'
          }`}
        >
          {isRunning ? <Pause size={28} /> : <Play size={28} />}
        </button>
        <button
          onClick={resetTimer}
          className={`p-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
            isRunning
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-gray-600 hover:bg-gray-700 text-white'
          }`}
          disabled={isRunning}
        >
          <RotateCcw size={28} />
        </button>
      </div>
    </div>
  );
}
